import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, errMsg, formatPrice } from "../../lib/api";
import { ArrowLeft, Send, Truck, Package, CheckCircle, Mail } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["paid", "packed", "shipped", "out_for_delivery", "delivered", "cancelled"];
const STATUS_LABELS = {
  paid: "Paid", packed: "Packed", shipped: "Shipped",
  out_for_delivery: "Out for Delivery", delivered: "Delivered", cancelled: "Cancelled"
};

export default function AdminOrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: "", tracking_number: "", note: "", notify: true });
  const [emailForm, setEmailForm] = useState({ subject: "", message: "" });
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    const { data } = await api.get(`/admin/orders/${id}`);
    setOrder(data);
    setStatusForm((f) => ({ ...f, status: data.status, tracking_number: data.tracking_number || "" }));
  };

  useEffect(() => { reload().catch(() => setOrder(null)); /* eslint-disable-next-line */ }, [id]);

  if (!order) return <div className="text-white/60">Loading order…</div>;

  const onUpdateStatus = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/admin/orders/${id}/status`, {
        status: statusForm.status,
        tracking_number: statusForm.tracking_number || null,
        note: statusForm.note || null,
        notify: statusForm.notify,
      });
      toast.success("Status updated" + (statusForm.notify ? " — customer notified" : ""));
      setStatusForm((f) => ({ ...f, note: "" }));
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const onSendEmail = async (e) => {
    e.preventDefault();
    if (!emailForm.subject || !emailForm.message) return;
    setBusy(true);
    try {
      await api.post(`/admin/orders/${id}/email`, emailForm);
      toast.success(`Email sent to ${order.email}`);
      setEmailForm({ subject: "", message: "" });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Link to="/admin/orders" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white">
        <ArrowLeft size={14} /> Back to orders
      </Link>

      <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#B026FF] font-bold">Order</div>
          <h1 className="font-display font-extrabold text-3xl lg:text-5xl tracking-tighter text-white font-mono">{order.order_number}</h1>
          <div className="mt-2 text-sm text-white/50">Placed {new Date(order.created_at).toLocaleString()}</div>
        </div>
        <div className="text-right">
          <div className="text-[11px] tracking-[0.22em] uppercase text-white/50 font-bold">Total</div>
          <div className="font-mono text-3xl text-white">{formatPrice(order.total_cents)}</div>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-7 space-y-6">
          {/* Items */}
          <section className="card-elev rounded-xl p-6">
            <h2 className="font-display text-xl text-white">Items</h2>
            <ul className="mt-4 divide-y divide-white/5">
              {order.items.map((it) => (
                <li key={it.product_id} className="py-3 flex items-center gap-4">
                  <div className="flex-1">
                    <div className="text-[10px] tracking-[0.22em] uppercase text-[#B026FF]/80 font-bold">{it.category?.replace(/-/g, ' ')}</div>
                    <div className="text-white">{it.name}</div>
                  </div>
                  <div className="text-white/60 font-mono text-sm">× {it.quantity}</div>
                  <div className="font-mono text-white">{formatPrice(it.line_total_cents)}</div>
                </li>
              ))}
            </ul>
            <div className="mt-5 pt-5 border-t border-white/5 space-y-2 max-w-sm ml-auto text-sm">
              <Row label="Subtotal" value={formatPrice(order.subtotal_cents)} />
              {order.discount_cents > 0 && <Row label="Kavo Points" value={`- ${formatPrice(order.discount_cents)}`} accent />}
              <Row label="Shipping" value={order.shipping_cents === 0 ? "Free" : formatPrice(order.shipping_cents)} />
              <Row label="Tax" value={formatPrice(order.tax_cents)} />
              <div className="flex justify-between text-base pt-2 border-t border-white/5">
                <span className="text-white">Total</span>
                <span className="font-mono text-white">{formatPrice(order.total_cents)}</span>
              </div>
            </div>
          </section>

          {/* Customer + shipping */}
          <section className="card-elev rounded-xl p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="text-[11px] tracking-[0.22em] uppercase text-[#B026FF] font-bold">Customer</div>
              <div className="mt-2 text-white">{order.name}</div>
              <div className="text-sm text-white/60">{order.email}</div>
              <div className="mt-3 text-xs text-white/50">
                {order.guest ? "Guest checkout" : "Authenticated customer"}
                {order.points_earned ? ` · earned ${order.points_earned} pts` : ""}
                {order.redeem_points ? ` · redeemed ${order.redeem_points} pts` : ""}
              </div>
            </div>
            <div>
              <div className="text-[11px] tracking-[0.22em] uppercase text-[#B026FF] font-bold">Ship to</div>
              <div className="mt-2 text-white">{order.shipping?.address_line1}</div>
              <div className="text-sm text-white/60">
                {order.shipping?.city}, {order.shipping?.postal_code}<br />
                {order.shipping?.country}
              </div>
            </div>
          </section>

          {/* History */}
          <section className="card-elev rounded-xl p-6">
            <h2 className="font-display text-xl text-white">Status history</h2>
            <ol className="mt-5 space-y-4">
              {(order.status_history || []).map((h, i) => (
                <li key={i} className="flex items-start gap-4">
                  <div className="w-2 h-2 rounded-full bg-[#B026FF] mt-2" />
                  <div className="flex-1">
                    <div className="text-white text-sm uppercase tracking-wider font-bold">{(h.status || "").replace(/_/g, ' ')}</div>
                    <div className="text-xs text-white/50 font-mono">{new Date(h.at).toLocaleString()}</div>
                    {h.note && <div className="text-sm text-white/70 mt-1">{h.note}</div>}
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </div>

        {/* Right rail — actions */}
        <div className="col-span-12 lg:col-span-5 space-y-6">
          <section className="card-elev rounded-xl p-6">
            <h2 className="font-display text-xl text-white flex items-center gap-2"><Truck size={18} className="text-[#B026FF]" /> Update status</h2>
            <form onSubmit={onUpdateStatus} className="mt-5 space-y-4">
              <Field label="Status">
                <select value={statusForm.status} onChange={(e) => setStatusForm((f) => ({ ...f, status: e.target.value }))}
                  data-testid="admin-status-select" className={inputCls}>
                  {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                </select>
              </Field>
              <Field label="Tracking number (optional)">
                <input value={statusForm.tracking_number} onChange={(e) => setStatusForm((f) => ({ ...f, tracking_number: e.target.value }))}
                  data-testid="admin-tracking-input" placeholder="1Z999AA10123456784" className={inputCls} />
              </Field>
              <Field label="Internal note (optional)">
                <input value={statusForm.note} onChange={(e) => setStatusForm((f) => ({ ...f, note: e.target.value }))}
                  data-testid="admin-status-note" className={inputCls} />
              </Field>
              <label className="flex items-center gap-2 text-sm text-white/80">
                <input type="checkbox" checked={statusForm.notify} onChange={(e) => setStatusForm((f) => ({ ...f, notify: e.target.checked }))}
                  data-testid="admin-status-notify" className="accent-[#B026FF]" />
                Email customer about this status
              </label>
              <button disabled={busy} data-testid="admin-status-submit" className="btn-violet w-full h-11 rounded-md text-sm disabled:opacity-50">
                {busy ? "Saving…" : "Update Status"}
              </button>
            </form>
          </section>

          <section className="card-elev rounded-xl p-6">
            <h2 className="font-display text-xl text-white flex items-center gap-2"><Mail size={18} className="text-[#B026FF]" /> Send custom email</h2>
            <form onSubmit={onSendEmail} className="mt-5 space-y-4">
              <Field label="Subject">
                <input required value={emailForm.subject} onChange={(e) => setEmailForm((f) => ({ ...f, subject: e.target.value }))}
                  data-testid="admin-email-subject" className={inputCls} />
              </Field>
              <Field label="Message">
                <textarea required rows={5} value={emailForm.message} onChange={(e) => setEmailForm((f) => ({ ...f, message: e.target.value }))}
                  data-testid="admin-email-message" className={inputCls + " resize-none"} />
              </Field>
              <button disabled={busy} data-testid="admin-email-send" className="btn-violet w-full h-11 rounded-md text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
                <Send size={14} /> Send to {order.email}
              </button>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full bg-[#110E1D] border border-white/10 rounded-md px-3 py-2.5 text-sm text-white outline-none focus:border-[#B026FF]/60";
function Field({ label, children }) {
  return <label className="block"><div className="block text-[11px] tracking-[0.22em] uppercase text-white/50 font-bold mb-2">{label}</div>{children}</label>;
}
function Row({ label, value, accent }) {
  return <div className="flex justify-between"><span className="text-white/60">{label}</span><span className={`font-mono ${accent ? "text-[#B026FF]" : "text-white"}`}>{value}</span></div>;
}
